/**
 * Order Service
 *
 * Service layer for order-related operations.
 * Abstracts Supabase RPC calls and direct queries for orders.
 */

import { supabase } from "./supabase";
import { logger } from "../utils/logger";
import type { Order, Database } from "./database.types";

/**
 * Order item input type
 */
export interface OrderItemInput {
  menu_item_id?: string | null;
  product_id?: string | null;
  variant_id?: string | null;
  combination_id?: string | null;
  variant_metadata?: Record<string, unknown> | null;
  variant_snapshot?: Record<string, unknown> | null;
  variant_display?: string | null;
  variantId?: string | null;
  combinationId?: string | null;
  variantMetadata?: Record<string, unknown> | null;
  quantity: number;
  price_at_purchase: number;
}

/**
 * Shipping address type
 */
export interface ShippingAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  [key: string]: unknown;
}

/**
 * Order creation data
 */
export interface OrderData {
  userId: string | null;
  customerEmail: string;
  customerName: string;
  shippingAddress: ShippingAddress;
  items: OrderItemInput[];
  discountCodeId?: string | null;
  discountAmount?: number;
  guestSessionId?: string | null;
  isGuest?: boolean | null;
}

/**
 * Order response type
 */
export interface OrderResponse {
  success: boolean;
  orderId: string | null;
  error: string | null;
}

/**
 * Orders response type
 */
export interface OrdersResponse {
  success: boolean;
  data: Order[] | null;
  error: string | null;
}

/**
 * Order query options
 */
export interface OrderQueryOptions {
  limit?: number;
  status?: string;
}

/**
 * Order filters
 */
export interface OrderFilters {
  status?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

/**
 * Create order with items using RPC function (atomic transaction)
 *
 * This is the recommended way to create orders as it:
 * - Validates prices on server-side (security)
 * - Creates order and items atomically (all or nothing)
 * - Calculates totals on server (prevents manipulation)
 * - Checks stock availability before creating order
 *
 * @param orderData - Order details
 * @returns Promise with order ID or error
 */
export async function createOrderWithItems(
  inputData: OrderData,
): Promise<OrderResponse> {
  try {
    const {
      userId,
      customerEmail,
      customerName,
      shippingAddress,
      items,
      discountAmount = 0,
    } = inputData;

    // Validate required fields
    if (!customerEmail || customerEmail.trim() === "") {
      return {
        success: false,
        orderId: null,
        error: "Customer email is required",
      };
    }

    if (!customerName || customerName.trim() === "") {
      return {
        success: false,
        orderId: null,
        error: "Customer name is required",
      };
    }

    if (!shippingAddress) {
      return {
        success: false,
        orderId: null,
        error: "Shipping address is required",
      };
    }

    if (!items || !Array.isArray(items) || items.length === 0) {
      return {
        success: false,
        orderId: null,
        error: "Order must contain at least one item",
      };
    }

    const sanitizedItems: Array<{
      product_id: string | null;
      menu_item_id: string | null;
      quantity: number;
      price_at_purchase: number;
    }> = [];

    // Validate each item
    for (const item of items) {
      const itemMenuItemId = item.menu_item_id || null;
      const itemProductId = item.product_id || null;

      if (!itemMenuItemId && !itemProductId) {
        return {
          success: false,
          orderId: null,
          error: "Each item must have a menu_item_id or product_id",
        };
      }

      if (!item.quantity || item.quantity <= 0) {
        return {
          success: false,
          orderId: null,
          error: "Each item must have a valid quantity",
        };
      }

      if (!item.price_at_purchase || item.price_at_purchase <= 0) {
        return {
          success: false,
          orderId: null,
          error: "Each item must have a valid price",
        };
      }

      sanitizedItems.push({
        product_id: itemProductId,
        menu_item_id: itemMenuItemId,
        quantity: item.quantity,
        price_at_purchase: Number(item.price_at_purchase),
      });
    }

    // Calculate subtotal from items
    const calculatedSubtotal = sanitizedItems.reduce(
      (sum, item) => sum + item.price_at_purchase * item.quantity,
      0,
    );
    const orderTotal = Math.max(0, calculatedSubtotal - (discountAmount || 0));

    // Create order directly with type assertion
    const orderInsert = {
      user_id: userId || null,
      customer_email: customerEmail.trim(),
      customer_name: customerName.trim(),
      shipping_address: shippingAddress,
      order_total: orderTotal,
      status: "pending",
      payment_status: "pending",
    } as Database["public"]["Tables"]["orders"]["Insert"];

    const { data: createdOrder, error: orderError } = await supabase
      .from("orders")
      .insert(orderInsert)
      .select("id")
      .single();

    if (orderError || !createdOrder) {
      logger.error("Error creating order:", orderError);
      return {
        success: false,
        orderId: null,
        error: orderError?.message || "Failed to create order",
      };
    }

    // Insert order items
    const orderItems = sanitizedItems.map((item) => ({
      order_id: createdOrder.id,
      menu_item_id: item.menu_item_id,
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price_at_purchase,
    }));

    const { error: itemsError } = await supabase
      .from("order_items")
      .insert(orderItems);

    if (itemsError) {
      logger.error("Error creating order items:", itemsError);
      // Delete the order since items failed
      await supabase.from("orders").delete().eq("id", createdOrder.id);
      return {
        success: false,
        orderId: null,
        error: "Failed to add items to order",
      };
    }

    return {
      success: true,
      orderId: createdOrder.id,
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in createOrderWithItems:", err);
    return {
      success: false,
      orderId: null,
      error: "An unexpected error occurred while creating your order",
    };
  }
}

/**
 * Get user's orders
 *
 * @param userId - User ID
 * @param options - Optional parameters
 * @returns Promise with orders data or error
 */
export async function getUserOrders(
  userId: string,
  options: OrderQueryOptions = {},
): Promise<OrdersResponse> {
  try {
    if (!userId) {
      return {
        success: false,
        data: null,
        error: "User ID is required",
      };
    }

    let query = supabase
      .from("orders")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (options.status) {
      query = query.eq("status", options.status);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      logger.error("Error fetching orders:", error);
      return {
        success: false,
        data: null,
        error: error.message || "Failed to load orders",
      };
    }

    return {
      success: true,
      data: (data as Order[]) || [],
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in getUserOrders:", err);
    return {
      success: false,
      data: null,
      error: "An unexpected error occurred",
    };
  }
}

/**
 * Get guest orders by email and session ID
 *
 * @param email - Guest email
 * @param sessionId - Guest session ID
 * @returns Promise with orders data or error
 */
export async function getGuestOrders(
  email: string,
  sessionId: string,
): Promise<OrdersResponse> {
  try {
    if (!email || !sessionId) {
      return {
        success: false,
        data: null,
        error: "Email and session ID are required",
      };
    }

    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .eq("customer_email", email)
      .eq("guest_session_id", sessionId)
      .order("created_at", { ascending: false });

    if (error) {
      logger.error("Error fetching guest orders:", error);
      return {
        success: false,
        data: null,
        error: error.message || "Failed to load orders",
      };
    }

    return {
      success: true,
      data: (data as Order[]) || [],
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in getGuestOrders:", err);
    return {
      success: false,
      data: null,
      error: "An unexpected error occurred",
    };
  }
}

/**
 * Get order by ID
 *
 * @param orderId - Order ID
 * @returns Promise with order data or error
 */
export async function getOrderById(orderId: string): Promise<{
  success: boolean;
  data: Order | null;
  error: string | null;
}> {
  try {
    if (!orderId) {
      return {
        success: false,
        data: null,
        error: "Order ID is required",
      };
    }

    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .eq("id", orderId)
      .single();

    if (error) {
      logger.error("Error fetching order:", error);
      return {
        success: false,
        data: null,
        error: error.message || "Failed to load order",
      };
    }

    return {
      success: true,
      data: data as Order,
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in getOrderById:", err);
    return {
      success: false,
      data: null,
      error: "An unexpected error occurred",
    };
  }
}

/**
 * Get all orders (admin only)
 *
 * @param filters - Optional filters
 * @returns Promise with orders data or error
 */
export async function getAllOrders(
  filters: OrderFilters = {},
): Promise<OrdersResponse & { count?: number }> {
  try {
    // Optimize: Only select needed fields instead of *
    let query = supabase
      .from("orders")
      .select(
        "id, status, order_total, created_at, customer_email, customer_name, user_id",
        {
          count: "exact",
        },
      )
      .order("created_at", { ascending: false });

    if (filters.status) {
      query = query.eq("status", filters.status);
    }

    if (filters.userId) {
      query = query.eq("user_id", filters.userId);
    }

    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    // Apply pagination
    if (filters.limit) {
      const offset = filters.offset || 0;
      query = query.range(offset, offset + filters.limit - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      logger.error("Error fetching orders:", error);
      return {
        success: false,
        data: null,
        error: error.message || "Failed to load orders",
        count: 0,
      };
    }

    return {
      success: true,
      data: (data as Order[]) || [],
      error: null,
      count: count || 0,
    };
  } catch (err) {
    logger.error("Unexpected error in getAllOrders:", err);
    return {
      success: false,
      data: null,
      error: "An unexpected error occurred",
      count: 0,
    };
  }
}

/**
 * Update order status
 *
 * @param orderId - Order ID
 * @param status - New status
 * @returns Promise with success status or error
 */
export async function updateOrderStatus(
  orderId: string,
  status: string,
): Promise<OrderResponse> {
  try {
    if (!orderId || !status) {
      return {
        success: false,
        orderId: null,
        error: "Order ID and status are required",
      };
    }

    const updateData = {
      status,
    } as Database["public"]["Tables"]["orders"]["Update"];
    const { data, error } = await supabase
      .from("orders")
      .update(updateData as never)
      .eq("id", orderId)
      .select("id")
      .single();

    if (error) {
      logger.error("Error updating order status:", error);
      return {
        success: false,
        orderId: null,
        error: error.message || "Failed to update order status",
      };
    }

    return {
      success: true,
      orderId: (data as { id: string } | null)?.id || null,
      error: null,
    };
  } catch (err) {
    logger.error("Unexpected error in updateOrderStatus:", err);
    return {
      success: false,
      orderId: null,
      error: "An unexpected error occurred",
    };
  }
}

// Export createOrder as alias for createOrderWithItems
export const createOrder = createOrderWithItems;

export default {
  createOrderWithItems,
  createOrder,
  getUserOrders,
  getGuestOrders,
  getOrderById,
  getAllOrders,
  updateOrderStatus,
};
